import {
  ARTICLE_CRITIQUE_DIMENSIONS,
  type ArticleCritiqueReport,
  critiqueDimensionLabel,
} from '../content-doctrine/article-critique';

/**
 * Tagging a person on an editorial review note (Reach > Editorial, a finding card in
 * `EditorialCritique`) sends them a Slack message about that note. This module is the pure
 * half, safe in the browser and on the server: the short note reference used in the link and
 * the message text. Delivery is `send-editorial-tag.ts` (server only); the route
 * (`server/api/slack/tag-note.post.ts`) resolves the workspace's Slack connection and hands
 * the bot token in, the same split `notify-on-slack.ts` uses.
 *
 * Kept deliberately small for v1: no log table, no in-app notification. The Slack message is
 * the record. See docs/planning/SPEC-editorial-mentions-2026-10-15.md for the fuller
 * assignee/comment design this does not attempt.
 */

export type CritiqueFinding = ArticleCritiqueReport['findings'][number];
export type CritiqueDimension = (typeof ARTICLE_CRITIQUE_DIMENSIONS)[number];

export function isCritiqueDimension(value: unknown): value is CritiqueDimension {
  return (
    typeof value === 'string' && (ARTICLE_CRITIQUE_DIMENSIONS as readonly string[]).includes(value)
  );
}

/**
 * A short, URL-safe reference for one finding. `articleCritiqueFindingKey` is the finding's
 * full dimension, quote and suggestion joined, far too long for a link, so the link carries
 * this FNV-1a hash of that same key instead. Pure and deterministic, so a page reading
 * `?finding=` can find the note by hashing each open finding's key and comparing.
 */
export function editorialFindingRef(findingKey: string): string {
  let hash = 0x811c9dc5;
  for (let index = 0; index < findingKey.length; index += 1) {
    hash ^= findingKey.charCodeAt(index);
    hash = Math.imul(hash, 0x01000193);
  }
  return (hash >>> 0).toString(36);
}

/** The relative path of the note in the app; the route prefixes the server's own public origin. */
export function editorialNotePath(topicId: string, findingRef: string): string {
  const params = new URLSearchParams({ topic: topicId, finding: findingRef });
  return `/reach/editorial?${params.toString()}`;
}

/** Slack mrkdwn treats these three characters as control syntax; everything else is literal. */
function escapeSlack(text: string): string {
  return text.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
}

/** One line, cut at a word boundary with an ellipsis, so a long passage cannot flood a DM. */
export function trimForSlack(text: string, max: number): string {
  const flat = text
    .replace(/\[([^\]]*)\]\([^)]*\)/g, '$1')
    .replace(/\s+/g, ' ')
    .trim();
  if (flat.length <= max) return flat;
  const cut = flat.slice(0, max - 1);
  const lastSpace = cut.lastIndexOf(' ');
  return `${(lastSpace > max * 0.6 ? cut.slice(0, lastSpace) : cut).trimEnd()}…`;
}

export const TAG_LIMITS = {
  quote: 280,
  reason: 300,
  suggestion: 300,
  note: 280,
  title: 160,
} as const;

export interface EditorialTagMessageInput {
  taggerName: string;
  articleTitle: string;
  dimension: string;
  reason: string;
  quote: string;
  suggestion: string;
  /** The tagger's own words, optional. */
  note?: string | null;
  /** Absolute URL back to the note. */
  link: string;
  /** Set when the message goes to a channel, so the tagged person is still mentioned by name. */
  mentionUserId?: string | null;
}

/** The Slack message text for a tagged note. Pure, so its shape is tested without Slack. */
export function buildEditorialTagMessage(input: EditorialTagMessageInput): string {
  const who = escapeSlack(trimForSlack(input.taggerName, 80) || 'A teammate');
  const title = escapeSlack(trimForSlack(input.articleTitle, TAG_LIMITS.title) || 'an article');
  const lead = input.mentionUserId
    ? `<@${input.mentionUserId}> ${who} tagged you on a review note in *${title}*`
    : `${who} tagged you on a review note in *${title}*`;
  const lines = [lead];
  const note = input.note ? trimForSlack(input.note, TAG_LIMITS.note) : '';
  if (note) lines.push(`${who}: “${escapeSlack(note)}”`);
  lines.push(
    `*${escapeSlack(critiqueDimensionLabel(input.dimension))}:* ${escapeSlack(
      trimForSlack(input.reason, TAG_LIMITS.reason),
    )}`,
  );
  const quote = trimForSlack(input.quote, TAG_LIMITS.quote);
  if (quote) lines.push(`> ${escapeSlack(quote)}`);
  const suggestion = trimForSlack(input.suggestion, TAG_LIMITS.suggestion);
  if (suggestion) lines.push(`Suggestion: ${escapeSlack(suggestion)}`);
  lines.push(`<${input.link}|Open the note in Mora>`);
  return lines.join('\n');
}
